import { useMemo, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { ChevronDown, Search, X } from "lucide-react";

// Seletor recolhível de múltipla seleção de Grupos (Ajuste Final de
// Permissões por Grupo) — substitui a lista de checkboxes exposta
// diretamente no modal, que não escalava para tenants com dezenas de
// Grupos. Regras de negócio (nenhum selecionado = sem restrição; só Grupos
// ativos entram como opção nova; vínculo histórico com Grupo inativado
// preservado) não mudam — só a apresentação. `groups` é a lista completa do
// tenant (ativos e inativos); `selected` é o array de ids já atribuídos ao
// usuário (pode legitimamente conter um Grupo hoje inativo, se atribuído
// antes da inativação).
export default function GroupMultiSelect({ groups, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  // Grupo inativo só aparece na lista quando já está selecionado — é o
  // vínculo histórico preservado; nunca é oferecido para uma seleção NOVA
  // (mesma regra já validada no backend em _validate_supervisor_group_ids).
  const listable = useMemo(
    () => groups.filter(g => g.active !== false || selected.includes(g.id)),
    [groups, selected],
  );
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return listable;
    return listable.filter(g => g.nome.toLowerCase().includes(term));
  }, [listable, q]);

  const namesById = useMemo(() => Object.fromEntries(groups.map(g => [g.id, g.nome])), [groups]);

  const toggle = (gid) => onChange(selected.includes(gid) ? selected.filter(x => x !== gid) : [...selected, gid]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(g => selected.includes(g.id));
  const selectAllVisible = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filtered.map(g => g.id));
      onChange(selected.filter(id => !filteredIds.has(id)));
    } else {
      onChange([...new Set([...selected, ...filtered.map(g => g.id)])]);
    }
  };

  // Resumo — usado tanto no botão fechado quanto como texto inicial (o
  // estado "nada selecionado" já É o resumo "sem restrição").
  const summary = useMemo(() => {
    if (selected.length === 0) return { primary: "Selecionar grupos", secondary: "" };
    const names = selected.map(id => namesById[id]).filter(Boolean);
    const shown = names.slice(0, 3);
    const rest = names.length - shown.length;
    const secondary = shown.join(", ") + (rest > 0 ? ` +${rest}` : "");
    const primary = selected.length === 1 ? "1 grupo selecionado" : `${selected.length} grupos selecionados`;
    return { primary, secondary };
  }, [selected, namesById]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline"
          className="w-full justify-between font-normal h-auto py-2"
          data-testid="perm-groups-trigger"
        >
          <span className="text-left min-w-0">
            <span className="block text-sm truncate">
              {selected.length === 0 ? "Nenhum grupo selecionado — sem restrição" : summary.primary}
            </span>
            {summary.secondary && <span className="block text-xs text-slate-500 font-normal truncate">{summary.secondary}</span>}
          </span>
          <ChevronDown size={14} className="opacity-60 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b border-slate-200">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              autoFocus value={q} onChange={e => setQ(e.target.value)}
              placeholder="Buscar grupo..." className="pl-8 h-8 text-sm" data-testid="perm-groups-search"
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <button
              type="button" onClick={selectAllVisible}
              className="text-xs text-primary hover:underline disabled:text-slate-300 disabled:no-underline"
              disabled={filtered.length === 0} data-testid="perm-groups-select-all"
            >
              {allFilteredSelected ? "Limpar seleção" : "Selecionar todos"}
            </button>
            {selected.length > 0 && (
              <button type="button" onClick={() => onChange([])} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-0.5" data-testid="perm-groups-clear">
                <X size={12} /> Limpar tudo
              </button>
            )}
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1" data-testid="perm-groups-list">
          {filtered.length === 0 && (
            <div className="text-xs text-slate-400 text-center py-6">
              {listable.length === 0 ? "Nenhum Grupo cadastrado ainda." : "Nenhum Grupo encontrado."}
            </div>
          )}
          {filtered.map(g => (
            <label key={g.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer transition-colors">
              <Checkbox checked={selected.includes(g.id)} onCheckedChange={() => toggle(g.id)} data-testid={`perm-group-${g.id}`} />
              <span className="text-sm truncate">{g.nome}</span>
              {g.active === false && <span className="text-xs text-slate-400 shrink-0">(inativo)</span>}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
